import type { NuxtError } from '#app';
import type { NloApiError } from '#imports';
import { NloError } from '#imports';
import type { components } from '~/generated/krasloten-api';
import { assertUnreachable } from '~/utils/exhaustiveness-check';

const GENERIC_ERROR_MESSAGE = 'Er is iets mis gegaan, probeer het later opnieuw.';

export function getKlError(input: NloApiError | NloError | any): NloError {
	if (input.data?._problemDetailsType === 'ErrorResultProblemDetails') {
		return new KlNloApiError(input);
	}

	return getNloError(input);
}

export function throwIfNotAuthenticated(error: NloError | NuxtError | Error | null | undefined | (NloError | NuxtError | Error | null)[]) {
	if (!error) {
		return;
	}

	const errors = Array.isArray(error) ? error : [error];
	for (const e of errors) {
		const klError = e instanceof NloError ? e : getKlError(e);
		if (klError instanceof NloNotAuthenticatedError) {
			throw klError;
		}
	}
}

export class KlNloApiError extends NloError {
	status: number;
	problemDetails?: components['schemas']['ErrorResultProblemDetails'];

	constructor(nloApiError: NloApiError) {
		const problemDetails = nloApiError.data as components['schemas']['ErrorResultProblemDetails'];
		super({ message: KlNloApiError.getErrorMessage(problemDetails), isFatal: nloApiError.status >= 500 });
		this.problemDetails = problemDetails;
		this.status = nloApiError.status;
	}

	/**
	 * Returns the error message for the given error code.
	 * Please note that this message is already available through KlNloApiError's message property.
	 * @param errorCode the error code from the C# API.
	 * @returns the error message (in Dutch) for the user
	 */
	static getErrorMessage(problemDetails?: components['schemas']['ErrorResultProblemDetails']): string {
		if (!problemDetails) {
			return GENERIC_ERROR_MESSAGE;
		}

		switch (problemDetails.errorCode) {
			case 'BarcodeAlreadyScannedByPlayerError': {
				return 'De barcode is al gescand.';
			}
			case 'BarcodeAlreadyScannedByOtherUser': {
				return 'De barcode is al gescand door iemand anders.';
			}
			case 'ProductNotFoundError': {
				return 'Het product is niet gevonden.';
			}
			case 'TicketExpiredError': {
				return 'Je ticket is verlopen.';
			}
			case 'TicketNotActivatedError': {
				return 'Dit kraslot is nog niet geactiveerd. Neem contact op met onze klantenservice op 070 550 08 52.';
			}
			case 'BarcodeInvalidError': {
				return 'Barcode ongeldig.';
			}
			case 'UserBlockedError': {
				return 'Er zijn meerdere foutieve scans gedetecteerd.';
			}
			case 'BalanceExceededError': {
				return 'Je hebt niet voldoende saldo in jouw wallet. Om te kunnen afrekenen moet je eerst het saldo opwaarderen.';
			}
			case 'BasketValidationError':
			case 'FreeTicketsInShoppingCartError': {
				return 'Er lijkt iets fout te gaan met de inhoud van jouw winkelmandje. Bekijk het en probeer het nog eens.';
			}
			case 'CachedOrderNotFoundError': {
				return 'Sorry, jouw bestelling kan niet gevonden worden. Probeer het nog eens.';
			}
			case 'MinimumOrderValueNotReachedError': {
				return typeof problemDetails.detail === 'string' ? problemDetails.detail : 'Let op: het minimale bestelbedrag is nog niet bereikt.';
			}
			case 'MaximumOrderValueExceededError': {
				return typeof problemDetails.detail === 'string' ? problemDetails.detail : 'Let op: het maximale bestelbedrag is bereikt.';
			}
			case 'PurchaseFailedError': {
				return 'Sorry, de transactie is mislukt. Probeer het nog eens.';
			}
			case 'TransactionAlreadyProcessedError': {
				return 'Er is iets mis gegaan, controleer in Mijn Krasloten of je bestelling verwerkt is.';
			}
			case 'ClaimFailedError': {
				return 'Er is iets misgegaan bij het inwisselen van je punten. Probeer het opnieuw.';
			}
			case 'InvalidProductForSweepstakeError': {
				return 'Het product is niet geldig voor deze actie.';
			}
			case 'RateLimitError':
			case 'RecaptchaNotValidError':
			case 'BarcodeCouldNotBeStoredError':
			case 'UserBlockedCheckFailedError':
			case 'BarcodeScanFailedUnexpectedError':
			case 'ActionExpiredError':
			case 'ActionNotFoundError':
			case 'ActionResponseError':
			case 'MaximumNumberOfParticipationsReachedError':
			case 'VoucherCodeAlreadyClaimedError':
			case 'VoucherCodeNotFoundError':
			case 'FormSubmitAlreadyParticipatedError':
			case 'FormSubmitValidationError':
			case 'InvalidSweepstakeError':
			case 'SweepstakeSpinError':
			case 'ParticipationLimitReachedError':
			case 'InvalidConfigurationError':
			case 'NoFileUploadedError':
			case undefined: {
				return GENERIC_ERROR_MESSAGE;
			}
			default:
				assertUnreachable(problemDetails.errorCode);
		}
	}
}
